import * as AgenticV2API from "@/services/api/agentic-v2";
import {
  createCase,
  triggerCaseCheckIngest,
  uploadCaseDocument,
} from "@/services/api/case-checking";
import { waitForCaseCheckTask } from "@/components/case-checking/waitForCaseCheckTask";

import type { NewCaseFileUploadFlowKind, NewCaseUploadedFile } from "./newCaseFileUploadTypes";

export type RunNewCaseFileUploadFlowOptions = {
  kind: NewCaseFileUploadFlowKind;
  onStep?: (step: string) => void;
  onFileRow?: (index: number, patch: Partial<NewCaseUploadedFile>) => void;
  openTaskProgress?: (taskIds: string[], title: string) => void;
};

export async function runNewCaseFileUploadFlow(
  selectedFiles: File[],
  { kind, onStep, onFileRow, openTaskProgress }: RunNewCaseFileUploadFlowOptions,
): Promise<{ caseId: string; uploadedDocIds: string[]; uploadedFileNames: string[] }> {
  if (selectedFiles.length === 0) {
    throw new Error("Please select at least one file");
  }

  let caseId: string;
  if (kind === "case_check_mission") {
    onStep?.("Creating case…");
    const mission = await AgenticV2API.createCaseCheckMission({});
    if (!mission.case_id) {
      throw new Error("Could not create case for file check.");
    }
    caseId = mission.case_id;
  } else {
    onStep?.("Creating case...");
    const newCase = await createCase({});
    caseId = newCase.id;
  }

  onStep?.("Uploading files...");
  const uploadedDocIds: string[] = [];
  const uploadedFileNames: string[] = [];
  const totalFiles = selectedFiles.length;

  for (let i = 0; i < selectedFiles.length; i++) {
    const file = selectedFiles[i];
    onStep?.(`Uploading ${i + 1} of ${totalFiles}...`);
    onFileRow?.(i, { classification: "Uploading...", status: "uploading" });
    try {
      const doc = await uploadCaseDocument(caseId, file);
      uploadedDocIds.push(doc.id);
      uploadedFileNames.push(file.name);
      onFileRow?.(i, { classification: "Queued for processing", status: "uploaded" });
    } catch (uploadErr) {
      console.error(`Failed to upload ${file.name}:`, uploadErr);
      onFileRow?.(i, { classification: "Upload failed", status: "pending" });
    }
  }

  if (uploadedDocIds.length === 0) {
    throw new Error("All file uploads failed. Please try again.");
  }

  onStep?.("Queuing document intake…");
  const ingestTask = await triggerCaseCheckIngest(caseId, {
    source: "uploaded",
    document_ids: uploadedDocIds,
  });
  openTaskProgress?.([ingestTask.task_id], "Processing documents");
  await waitForCaseCheckTask(ingestTask.task_id);

  onStep?.("");
  return { caseId, uploadedDocIds, uploadedFileNames };
}
